import { useQuery } from '@tanstack/react-query'
import {
  PiArchive,
  PiArrowCounterClockwise,
  PiCalendar,
  PiDotsThreeVerticalBold,
  PiEye,
  PiPencilSimple,
  PiPlus,
  PiRocket,
  PiUser,
  PiUsers,
} from 'react-icons/pi'
import { TargetArrowIcon } from 'components/Layout/LeftNav/nav-icons'
import { useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'

import { useQueryClient } from '@tanstack/react-query'
import FbButton from 'components/FbUI/FbButton'
import { FBPage } from 'components/FbUI/Page/FBPage'
import {
  FBPageHeader,
  FBPageInnerTabsContainer,
  FBPageTableContainer,
  FBPageTitle,
  FBPageTitleRow,
} from 'components/FbUI/Page/styles'
import { UnderlineTabs } from 'components/FbUI/UnderlineTabs'
import { ContactRequestJobFromCampaignModal } from 'components/Modals/ContactRequestJobFromCampaignModal'
import { ContactRequestsTable } from 'components/Tables/ContactRequestsTable/ContactRequestsTable'
import { cn } from 'components/UI/cn'
import {
  CONTACT_REQUEST_JOB_TABLE_KEY,
  getCampaignTableKey,
} from 'constants/tableQueryKeys'
import { useEntityBreadcrumbs } from 'context/layout'
import { ArchiveCampaignModal } from 'features/campaigns/ArchiveCampaignModal'
import { useGetCampaignSettings } from 'features/campaigns/campaign_settings_api'
import { CampaignDetailContactsTab } from 'features/campaigns/CampaignDetailContactsTab'
import { CampaignDetailOverviewTab } from 'features/campaigns/CampaignDetailOverviewTab'
import { CampaignDetailTargetsTab } from 'features/campaigns/CampaignDetailTargetsTab'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { StageColorProvider } from 'components/SalesStage/StageColorContext'
import {
  useArchiveCampaign,
  useGetCampaignById,
  useGetCampaignCompanies,
  useUnarchiveCampaign,
} from 'features/campaigns/campaigns_api'
import { EditCampaignModalV2 } from 'features/campaigns/EditCampaignModalV2'
import { useGetCampaignSequence } from 'features/campaigns/sequences_api'
import { WORKFLOW_STATUS } from 'models/campaign'
import { ALLOWED_CONTACT_REQUEST_TYPES } from 'models/companies'
import apiService from 'services/api'
import { NotFoundContent } from '../not-found-content'

type DetailTab = 'overview' | 'targets' | 'contacts' | 'contact_requests'

export function CampaignDetailPageV2() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<DetailTab>('overview')
  const [showOverflowMenu, setShowOverflowMenu] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showContactRequestModal, setShowContactRequestModal] = useState(false)
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const archiveMutation = useArchiveCampaign()
  const unarchiveMutation = useUnarchiveCampaign()
  const api = apiService()
  const campaignId = parseInt(id ?? '-1')
  const { data: campaign, isLoading } = useGetCampaignById(campaignId)
  const { data: sequence } = useGetCampaignSequence(campaignId)
  const { data: settings } = useGetCampaignSettings(campaignId)
  const { data: campaignCompanies } = useGetCampaignCompanies(campaignId)
  const { data: contactsData } = useQuery({
    queryKey: ['campaign', campaignId, 'contacts-count'],
    queryFn: () =>
      api.getContactsList({
        campaign: campaignId,
        campaign_page: true,
        type: 'all',
        limit: 1,
        page: 1,
      }),
    enabled: !!campaign,
    staleTime: 1000 * 60 * 5,
  })
  const breadcrumbs = useMemo(
    () => (campaign ? [{ label: campaign.name }] : []),
    [campaign]
  )
  useEntityBreadcrumbs(breadcrumbs)

  const hasContactRequests = useMemo(() => {
    if (!campaign) return false
    // Multi-type campaigns (type is null) always show contact requests — the
    // subcategory picker inside the wizard handles per-type eligibility.
    if (!campaign.type) return true
    return ALLOWED_CONTACT_REQUEST_TYPES.includes(campaign.type.slug as any)
  }, [campaign])
  const { data: contactRequestsData } = useQuery({
    queryKey: ['campaign', campaignId, 'contact-requests-count'],
    queryFn: async () =>
      api.fetchContactRequestJobs({ campaign: campaignId, limit: 1, page: 1 }),
    enabled: !!campaign && hasContactRequests,
    staleTime: 1000 * 60 * 5,
  })

  const targetCount = campaignCompanies?.total_count ?? 0
  const contactCount = contactsData?.count ?? 0
  const totalContactRequestsCount = contactRequestsData?.count ?? 0

  const tabsWithCounts: {
    key: DetailTab
    title: string
    count?: number | null
  }[] = useMemo(() => {
    const tabs: {
      key: DetailTab
      title: string
      count?: number | null
    }[] = [
      { key: 'overview', title: 'Overview' },
      { key: 'targets', title: 'Targets', count: targetCount },
      { key: 'contacts', title: 'Contacts', count: contactCount },
    ]
    if (hasContactRequests) {
      tabs.push({
        key: 'contact_requests',
        title: 'Contact Requests',
        count: totalContactRequestsCount,
      })
    }
    return tabs
  }, [targetCount, contactCount, hasContactRequests, totalContactRequestsCount])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner animation="border" />
      </div>
    )
  }
  if (!campaign) {
    return <NotFoundContent />
  }

  const formattedLaunchDate = campaign.launch_date
    ? new Date(campaign.launch_date).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Not set'
  const hasTargets = targetCount > 0
  const hasContacts = contactCount > 0
  const hasSequence = !!sequence
  const hasLaunchDate = !!campaign.launch_date
  const campaignStatus = settings?.status ?? 'draft'
  const isActiveCampaign =
    campaignStatus === 'active' ||
    campaignStatus === 'queued' ||
    campaignStatus === 'launching'
  const isPausedCampaign = campaignStatus === 'paused'
  const sequenceButtonLabel = isActiveCampaign
    ? 'View Sequence'
    : isPausedCampaign
      ? 'Edit Sequence'
      : !hasSequence
        ? 'Create Sequence'
        : hasContacts && hasLaunchDate
          ? 'Preview & Launch'
          : 'Continue Setup'
  const SequenceButtonIcon = isActiveCampaign
    ? PiEye
    : isPausedCampaign
      ? PiPencilSimple
      : !hasSequence
        ? PiPlus
        : hasContacts && hasLaunchDate
          ? PiRocket
          : PiPencilSimple
  const statusConfig: Record<string, { label: string; className: string }> = {
    draft: {
      label: 'Draft',
      className: 'bg-gray-100 text-gray-700',
    },
    active: {
      label: 'Active',
      className: 'bg-green-50 text-green-700',
    },
    launching: {
      label: 'Launching',
      className: 'bg-green-50 text-green-700',
    },
    queued: {
      label: 'Queued',
      className: 'bg-green-50 text-green-700',
    },
    paused: {
      label: 'Paused',
      className: 'bg-amber-50 text-amber-700',
    },
    completed: {
      label: 'Completed',
      className: 'bg-green-50 text-green-700',
    },
    stopped: {
      label: 'Stopped',
      className: 'bg-red-50 text-red-700',
    },
    failed: {
      label: 'Failed',
      className: 'bg-red-50 text-red-700',
    },
    archived: {
      label: 'Archived',
      className: 'bg-gray-100 text-gray-700',
    },
    'pending deletion': {
      label: 'Pending Deletion',
      className: 'bg-red-50 text-red-700',
    },
    deleted: {
      label: 'Deleted',
      className: 'bg-gray-100 text-gray-700',
    },
  }
  const badge = statusConfig[campaignStatus] ?? statusConfig.draft

  return (
    <>
      <FBPage
        innerPageClassName="!bg-[#fefefe]"
        containerClassName="!bg-[#fefefe] !shadow-none"
      >
        <FBPageHeader>
          <FBPageTitleRow className="w-auto flex-col !items-start gap-1">
            <span
              className={cn(
                'inline-flex items-center rounded-2xl border-0 px-2.5 py-0.5 text-sm font-medium leading-5 tracking-[-0.2px]',
                badge.className
              )}
              style={
                campaignStatus === 'draft'
                  ? { backgroundColor: '#f3f4f6', color: '#374151' }
                  : undefined
              }
            >
              {badge.label}
            </span>
            <FBPageTitle>
              {campaign.name}
              {campaign.type && (
                <span className="ml-2 align-middle text-base font-normal text-gray-500">
                  · {campaign.type.name}
                </span>
              )}
            </FBPageTitle>
          </FBPageTitleRow>
          <div className="flex items-center gap-3">
            <FbButton
              onClick={() => navigate(`/campaigns/${id}/sequence`)}
              className="!bg-brand-bright !text-white hover:!bg-brand-active whitespace-nowrap"
            >
              <SequenceButtonIcon className="size-4" />
              <span>{sequenceButtonLabel}</span>
            </FbButton>
            <div className="relative">
              <FbButton
                onClick={() => setShowOverflowMenu(!showOverflowMenu)}
                variant="secondary"
                className="!border-neutral-300 !text-neutral-800 hover:!bg-neutral-100 hover:!text-neutral-900 !px-2.5"
                aria-label="More actions"
              >
                <PiDotsThreeVerticalBold className="size-4" />
              </FbButton>
              {showOverflowMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowOverflowMenu(false)}
                  />
                  <div className="absolute right-0 top-full z-20 mt-1.5 w-48 overflow-hidden rounded-lg border border-line bg-surface py-1 shadow-lg">
                    <button
                      type="button"
                      onClick={() => {
                        setShowOverflowMenu(false)
                        setShowEditModal(true)
                      }}
                      className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-fg transition-colors hover:bg-surface-hover"
                    >
                      <PiPencilSimple className="size-4 text-fg-secondary" />
                      Edit Campaign
                    </button>
                    {hasContactRequests && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowOverflowMenu(false)
                          setShowContactRequestModal(true)
                        }}
                        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-fg transition-colors hover:bg-surface-hover"
                      >
                        <PiUsers className="size-4 text-fg-secondary" />
                        Request Contacts
                      </button>
                    )}
                    {campaign.is_archived ? (
                      <button
                        type="button"
                        onClick={() => {
                          setShowOverflowMenu(false)
                          unarchiveMutation.mutate(campaign.id)
                        }}
                        disabled={unarchiveMutation.isPending}
                        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-fg transition-colors hover:bg-surface-hover"
                      >
                        <PiArrowCounterClockwise className="size-4 text-fg-secondary" />
                        Unarchive Campaign
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setShowOverflowMenu(false)
                          setShowArchiveConfirm(true)
                        }}
                        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-fg transition-colors hover:bg-surface-hover"
                      >
                        <PiArchive className="size-4 text-fg-secondary" />
                        Archive Campaign
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </FBPageHeader>
        <div className="flex items-center gap-6 mb-4">
          <span className="flex items-center gap-1.5 text-sm text-gray-500 tracking-[-0.2px]">
            <PiCalendar className="size-4 text-brand-active" />
            Projected:{' '}
            <span className="font-medium text-gray-900">
              {formattedLaunchDate}
            </span>
          </span>
          {hasTargets && (
            <span className="flex items-center gap-1.5 text-sm text-gray-500 tracking-[-0.2px]">
              <TargetArrowIcon size={16} className="text-brand-active" />
              Targets:{' '}
              <span className="font-medium text-gray-900">{targetCount}</span>
            </span>
          )}
          {campaign.owner_name && (
            <span className="flex items-center gap-1.5 text-sm text-gray-500 tracking-[-0.2px]">
              <PiUser className="size-4 text-brand-active" />
              Owner:{' '}
              <span className="font-medium text-gray-900">
                {campaign.owner_name}
              </span>
            </span>
          )}
        </div>
        <FBPageInnerTabsContainer>
          <UnderlineTabs<DetailTab>
            tabs={tabsWithCounts}
            activeKey={activeTab}
            onChange={setActiveTab}
            idPrefix="campaign-detail"
            aria-label="Campaign sections"
          />
        </FBPageInnerTabsContainer>
        <div className="flex flex-col flex-1 min-h-0">
          {activeTab === 'overview' && (
            <CampaignDetailOverviewTab
              campaign={campaign}
              campaignId={id!}
              hasSequence={hasSequence}
            />
          )}
          {activeTab === 'targets' && (
            <CampaignDetailTargetsTab campaign={campaign} />
          )}
          {activeTab === 'contacts' && (
            <CampaignDetailContactsTab
              campaign={campaign}
              onSwitchToTargets={() => setActiveTab('targets')}
              onContactRequested={() => {
                void queryClient.invalidateQueries({
                  queryKey: ['campaign', campaignId, 'contact-requests-count'],
                })
              }}
            />
          )}
          {activeTab === 'contact_requests' && (
            <TableLinkStyleProvider variant="plain">
              <StageColorProvider value="pipeline">
                <FBPageTableContainer
                  className="overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]"
                  style={{
                    height: 'calc(var(--base-page-table-height) - 102px)',
                  }}
                >
                  <ContactRequestsTable
                    tableKey={getCampaignTableKey(
                      CONTACT_REQUEST_JOB_TABLE_KEY,
                      campaign.id
                    )}
                    campaignId={campaign.id}
                  />
                </FBPageTableContainer>
              </StageColorProvider>
            </TableLinkStyleProvider>
          )}
        </div>
      </FBPage>
      <EditCampaignModalV2
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        campaign={campaign}
        campaignId={campaign.id}
      />
      <ContactRequestJobFromCampaignModal
        campaignId={campaign.id}
        isMultiType={!campaign.type}
        externalOpen={showContactRequestModal}
        onExternalClose={() => setShowContactRequestModal(false)}
        onContactRequested={() => {
          void queryClient.invalidateQueries({
            queryKey: ['campaign', campaignId, 'contact-requests-count'],
          })
        }}
      />
      <ArchiveCampaignModal
        show={showArchiveConfirm}
        onHide={() => setShowArchiveConfirm(false)}
        campaignName={campaign.name}
        isActive={campaignStatus === WORKFLOW_STATUS.ACTIVE}
        onConfirm={() => {
          archiveMutation.mutate(campaign.id, {
            onSuccess: () => navigate('/campaigns'),
          })
        }}
        isLoading={archiveMutation.isPending}
      />
    </>
  )
}
